import { Parameter } from "domain/entities/parameter";
import { DocumentTypeInputFilter } from "domain/models/document-type-input-filter";
import { useDocumentTypeByFilter } from "presentation/hooks/use-query-document-type";
import { DocumentType } from "domain/entities/document-type";
import { Document } from "domain/entities/document";
import { Dialog } from "primereact/dialog";
import { RefObject, useEffect, useState } from "react";
import { Button } from "primereact/button";
import MobileDropdownComponent from "../../../../components/MobileDropdownComponent";
import MobileInputDateComponent from "../../../../components/MobileInputDateComponent";
import { httpClient } from "infra/axios/axios-api";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useCreateCPSDocumentMutation } from "presentation/hooks/use-query-document";
import { EMAIL } from "domain/enum/email";
import { Toast } from "primereact/toast";
import Spinner from "presentation/components/Shared/Spinner";
import { QueryObserverResult } from "@tanstack/react-query";
import { Message } from "domain/entities/message";
import MobilePDFViewer from "presentation/mobile/components/MobilePDFViewer";
import MobileDocumentUploader from "presentation/mobile/components/MobileDocumentUploader";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";
import { getApiErrorMessage } from "presentation/mobile/utils/api-error.util";

interface DocumentTypeOption {
    id: string;
    value: string;
    type: string;
}

interface InvoiceParams {
    date: Date;
    documentType: DocumentTypeOption;
}

export interface RegisterInvoiceProps {
    visible: boolean;
    setVisible: (isVisible: boolean) => void;
    toastRef?: RefObject<Toast | null>;
    refetch: () => Promise<QueryObserverResult<Message<Document[]> | undefined, Error>>;
}

const RegisterInvoice = ({ visible, setVisible, toastRef, refetch }: RegisterInvoiceProps) => {
    const { email: userEmployeeEmail, userEmployee } = useAuthStore();

    const [documentTypeOptions, setDocumentTypeOptions] = useState<Parameter[]>([]);
    const [doc, setDoc] = useState<{ file: File; name: string; url: string } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [invoiceParameters, setInvoiceParameters] = useState<InvoiceParams>();
    const [showDoc, setShowDoc] = useState(false);

    const { mutateAsync: createCPSDocumentMutation } =
        useCreateCPSDocumentMutation();

    const { data: documentTypes } = useDocumentTypeByFilter({
        limit: 100,
        justCPStypes: true,
    } as DocumentTypeInputFilter);

    useEffect(() => {
        if (!documentTypes?.data) return;

        const filtered = documentTypes.data
            .filter((type: DocumentType) => {
                const name = type.name.toLowerCase();
                return name === "recibo" || name === "fatura" || name === "fatura/recibo";
            })
            .map((type: DocumentType) => ({
                id: type.id,
                value: type.name,
                type: type.name,
            }));

        setDocumentTypeOptions(filtered);
    }, [documentTypes?.data]);

    // Devolve true se a fatura foi guardada (o save() decide se fecha o modal)
    const handleSave = async (): Promise<boolean> => {
        if (!doc || !invoiceParameters?.date || !invoiceParameters.documentType) return false;

        const month = new Date(invoiceParameters.date).getMonth() + 1;
        const year = new Date(invoiceParameters.date).getFullYear();

        const formData = new FormData();
        const extension = doc.name.split(".").pop();
        const safeDocName = `${month}_${year}_${invoiceParameters.documentType.id}_${Date.now()}`;
        const fileNameWithExtension = `${safeDocName}.${extension}`;
        formData.append("file", doc.file, fileNameWithExtension);

        try {
            const response = await httpClient.post(
                `/assets/invoices/${userEmployee?.id}`,
                formData
            );

            const cpsDocumentToCreate: Document = {
                id: undefined,
                fullPath: response.data.filePath,
                documentType: { id: invoiceParameters.documentType.id },
                employee: { id: userEmployee?.id ?? "" },
                month,
                year,
                lastModifiedBy: userEmployeeEmail ?? EMAIL,
            };

            await createCPSDocumentMutation(cpsDocumentToCreate);
            return true;
        } catch (error: unknown) {
            toastRef?.current?.show({
                severity: "error",
                summary: "Erro",
                detail: getApiErrorMessage(error, "Falha ao guardar documentos."),
                life: 3500,
            });
            return false;
        }
    };


    const cleanData = () => {
        setInvoiceParameters(undefined);
        setDoc(null);
    };

    // Closing mid-save would leave the request running with the form already cleared
    const close = () => {
        if (isSaving) return;
        cleanData();
        setVisible(false);
    };

    // isSaving covers the whole flow (upload, create and refetch) so a slow network can't trigger a second save
    const save = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const saved = await handleSave();
            if (!saved) return;

            toastRef?.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Documento guardado com sucesso.",
                life: 3500,
            });
            cleanData();
            await refetch();
            setVisible(false);
        } finally {
            setIsSaving(false);
        }
    };

    const footer = (
        <div className="w-full flex gap-2">
            <Button
                label="Cancelar"
                type="button"
                className="btn-mobile-secondary flex-1"
                disabled={isSaving}
                onClick={close}
            />
            <Button
                label="Registar Fatura"
                icon="pi pi-check"
                type="button"
                className="btn-mobile flex-1"
                disabled={isSaving || !doc || !invoiceParameters?.date || !invoiceParameters.documentType}
                loading={isSaving}
                onClick={save}
            />
        </div>
    );

    return (
        <MobileBottomSheet
            visible={visible}
            onHide={close}
            title="Registar Nova Fatura"
            headerIcon="pi pi-file"
            footer={footer}
        >
            {isSaving ? (

                <div className="flex justify-content-center align-items-center" style={{ minHeight: "260px" }}>
                    <Spinner />
                </div>
            ) : (
                <form className="w-full flex flex-column gap-3 py-2">
                    <div>
                        <label className="text-sm font-semibold mb-1 block" style={{ color: "#334155" }}>
                            Tipo de Documento
                        </label>
                        <MobileDropdownComponent
                            options={documentTypeOptions}
                            isValueId={true}
                            label="Tipo de Documento"
                            fieldName="documentType"
                            currentObject={invoiceParameters}
                            getValue={invoiceParameters?.documentType?.id}
                            setValue={setInvoiceParameters}
                        />
                    </div>

                    <div>
                        <label className="text-sm font-semibold mb-1 block" style={{ color: "#334155" }}>
                            Mês/Ano
                        </label>
                        <MobileInputDateComponent
                            fieldName="date"
                            view="month"
                            dateFormat="mm/yy"
                            currentObject={invoiceParameters}
                            getValue={invoiceParameters?.date}
                            setValue={setInvoiceParameters}
                        />
                    </div>

                    <div className="w-full">
                        <MobileDocumentUploader
                            value={doc}
                            onChange={setDoc}
                            label="Documento da Fatura *"
                            onView={() => setShowDoc(true)}
                        />
                    </div>
                </form>
            )}

            {/* Visualizador do ficheiro anexado */}
            <Dialog
                header="Visualização do Documento"
                visible={showDoc}
                style={{ width: "90vw", height: "85vh" }}
                onHide={() => setShowDoc(false)}
                maximized
            >
                {doc && (doc.name.includes(".pdf") ? (
                    <MobilePDFViewer url={doc.url} fileName={doc.name} />
                ) : (
                    <img src={doc.url} width="100%" alt="Fatura" height="auto" />
                ))}
            </Dialog>
        </MobileBottomSheet>
    );
};

export default RegisterInvoice;